import React from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';

export type RecipeContentRejectionVariant = 'not_recipe' | 'misconduct';

interface RecipeContentRejectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    variant: RecipeContentRejectionVariant;
    language?: Language;
}

const RecipeContentRejectionModal: React.FC<RecipeContentRejectionModalProps> = ({
    isOpen,
    onClose,
    variant,
    language,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);

    if (!isOpen) return null;

    const isMisconduct = variant === 'misconduct';

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[700] p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className={`relative w-full max-w-sm overflow-hidden rounded-2xl border shadow-2xl ${
                    isMisconduct
                        ? 'border-red-300/80 bg-red-50 dark:border-red-900/60 dark:bg-red-950/90'
                        : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                {isMisconduct && (
                    <div
                        className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.08] dark:opacity-[0.12]"
                        aria-hidden
                    >
                        <svg className="h-56 w-56 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
                            <path strokeLinecap="round" strokeWidth="1.5" d="M7 7l10 10M17 7L7 17" />
                        </svg>
                    </div>
                )}

                <div className="relative p-6 text-center">
                    <div
                        className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
                            isMisconduct
                                ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                                : 'bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400'
                        }`}
                    >
                        {isMisconduct ? (
                            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                <circle cx="12" cy="12" r="9" strokeWidth="2" />
                                <path strokeLinecap="round" strokeWidth="2" d="M8 8l8 8M16 8l-8 8" />
                            </svg>
                        ) : (
                            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </div>

                    <h3
                        className={`text-lg font-black ${
                            isMisconduct ? 'text-red-800 dark:text-red-200' : 'text-slate-800 dark:text-slate-100'
                        }`}
                    >
                        {isMisconduct ? t('recipeContentMisconductTitle') : t('recipeContentNotRecipeTitle')}
                    </h3>

                    <p
                        className={`mt-2 text-sm leading-relaxed ${
                            isMisconduct ? 'text-red-700/90 dark:text-red-200/90' : 'text-slate-500 dark:text-slate-400'
                        }`}
                    >
                        {isMisconduct ? t('recipeContentMisconductBody') : t('recipeContentNotRecipeMsg')}
                    </p>

                    {isMisconduct && (
                        <p className="mt-4 rounded-xl border border-red-200/80 bg-red-100/60 px-3 py-2.5 text-xs font-bold leading-relaxed text-red-800 dark:border-red-800/60 dark:bg-red-900/30 dark:text-red-200">
                            {t('recipeContentMisconductBlockingWarning')}
                        </p>
                    )}
                </div>

                <div className={`relative p-4 ${isMisconduct ? 'bg-red-100/50 dark:bg-red-950/50' : 'bg-slate-50 dark:bg-slate-800'}`}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={`w-full rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-all ${
                            isMisconduct
                                ? 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600'
                                : 'bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600'
                        }`}
                    >
                        {t('ok')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecipeContentRejectionModal;
